import { t } from '../i18n';
const VOLUME_KEY = 'viva.volume';

export interface VolumeState {
  volume: number;
  muted: boolean;
}

/** Saved volume; storage can be unavailable (private mode, TV quirks). */
export function readVolume(): VolumeState {
  try {
    const saved = JSON.parse(localStorage.getItem(VOLUME_KEY) ?? 'null') as Partial<VolumeState> | null;
    const volume = typeof saved?.volume === 'number' && saved.volume >= 0 && saved.volume <= 1 ? saved.volume : 1;
    return { volume, muted: saved?.muted === true };
  } catch {
    return { volume: 1, muted: false };
  }
}

export function saveVolume(state: VolumeState): void {
  try {
    localStorage.setItem(VOLUME_KEY, JSON.stringify(state));
  } catch {
    // Not remembering the volume only costs convenience.
  }
}

/** Mute toggle plus a 0–100 slider. */
export function VolumeControl({ state, onChange }: { state: VolumeState; onChange: (next: VolumeState) => void }) {
  const percent = Math.round(state.volume * 100);
  const silent = state.muted || percent === 0;
  return (
    <div className="volume">
      <button
        type="button"
        className="button"
        aria-pressed={state.muted}
        aria-label={state.muted ? t('player.unmute') : t('player.mute')}
        onClick={() => onChange({ volume: state.volume === 0 ? 0.5 : state.volume, muted: !state.muted })}
      >
        {silent ? '🔇' : percent < 50 ? '🔉' : '🔊'}
      </button>
      <input
        className="volume__slider"
        type="range"
        min={0}
        max={100}
        step={5}
        value={state.muted ? 0 : percent}
        aria-label={t('player.volume')}
        aria-valuetext={`${state.muted ? 0 : percent}%`}
        onChange={(event) => onChange({ volume: Number(event.target.value) / 100, muted: false })}
      />
      <span className="volume__value">{state.muted ? 0 : percent}%</span>
    </div>
  );
}
